import { WorkerRequirementStatus } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { openZendeskMessaging } from "@src/appV2/lib/ZendeskMessaging/openZendeskMessaging";
import { useGetRunBackgroundCheckOnBookingConfiguration } from "@src/appV2/redesign/BackgroundChecks/useGetRunBackgroundCheckOnBookingConfiguration";
import { TextButton } from "@src/appV2/redesign/components/TextButton";
import { Text } from "@src/appV2/redesign/system/ui-components";
import { useSupportLinks } from "@src/appV2/support/hooks/useSupportLinks";
import { useZendeskArticle } from "@src/appV2/Zendesk/useZendeskArticle";
import { useQueryClient } from "@tanstack/react-query";
import { parseISO } from "date-fns";
import { useRef } from "react";
import { generatePath, useHistory } from "react-router-dom";

import { Button } from "../../../../components/Button";
import { PageFooterWrapper } from "../../../../components/PageFooterWrapper";
import { PageWrapper } from "../../../../components/PageWrapper";
import { StickyScrollPageHeader } from "../../../../components/StickyScrollPageHeader";
import { invalidateWorkerRequirements } from "../../../api/invalidateWorkerRequirements";
import { DocumentStatusCard } from "../../../components/StatusCard";
import { DocumentStatusCardActionButton } from "../../../components/StatusCardActionButton";
import {
  DOCUMENT_CHECKR_ERROR_PATH,
  DOCUMENT_CHECKR_LOADING_PATH,
  DOCUMENT_DETAILS_FULL_PATH,
} from "../../../paths";
import { getCustomDocumentStatusCardContent } from "../../../utils/workerRequirementStatusContent";
import { workerRequirementStatusToDocumentCardStatus } from "../../../utils/workerRequirementStatusToDocumentCardStatus";
import { DocumentDetailsSection } from "../../DetailsSection";
import { DocumentDetailsSectionSkeleton } from "../../DetailsSectionSkeleton";
import { DocumentDetailsRequiredBySection } from "../../RequiredBySection/Section";
import { DocumentPageScrollWrapper } from "../../ScrollWrapper";
import { useRequirementDetailsContext } from "../../useRequirementDetailsContext";
import { useRequirementPrerequisiteActions } from "../../useRequirementPrerequisiteActions";
import { getCheckrRequirementStatus } from "./getCheckrRequirementStatus";
import {
  invalidateCheckrApplicationStatus,
  useGetCheckrApplicationStatus,
} from "./useGetCheckrApplicationStatus";
import { useOpenCheckrInvitation } from "./useOpenCheckrInvitation";

export function DocumentDetailsCheckrPage() {
  const { requirement: workerRequirement } = useRequirementDetailsContext();
  const {
    status: requirementStatus,
    requirement,
    expiry,
    requirementId,
    isValid,
  } = workerRequirement;
  const { name, instructions } = requirement?.attributes ?? {};

  const scrollRef = useRef<HTMLDivElement>(null);

  const history = useHistory();
  const queryClient = useQueryClient();
  const { openArticle } = useZendeskArticle();
  const { backgroundChecksSupportLink } = useSupportLinks();
  const openCheckrInvitation = useOpenCheckrInvitation();
  const { withPrerequisiteCheck, prerequisiteBottomSheet } = useRequirementPrerequisiteActions();

  const { data: checkrApplicationStatus, isLoading: isLoadingCheckrApplicationStatus } =
    useGetCheckrApplicationStatus();

  const { isEnabled: isRunBackgroundCheckOnBookingEnabled } =
    useGetRunBackgroundCheckOnBookingConfiguration();

  const status = getCheckrRequirementStatus({
    status: requirementStatus,
    checkrApplicationStatus: checkrApplicationStatus?.data,
    isRunBackgroundCheckOnBookingEnabled,
    isValid,
  });

  const documentCardStatus = workerRequirementStatusToDocumentCardStatus[status];

  const documentCardContent = getCustomDocumentStatusCardContent({
    isRunBackgroundCheckOnBookingEnabled,
    status,
    requirementWorkflowType: requirement?.attributes.requirementWorkflowType,
    resubmissionDisabledFor: requirement?.attributes.resubmissionDisabledFor,
  });

  const isActionRequired = [
    WorkerRequirementStatus.MISSING,
    WorkerRequirementStatus.EXPIRING,
    WorkerRequirementStatus.EXPIRED,
  ].includes(status);

  const shouldShowCta = isActionRequired && !isLoadingCheckrApplicationStatus;

  return (
    <PageWrapper variant="tertiary">
      <StickyScrollPageHeader
        title={name ?? ""}
        headerVariant="tertiary"
        scrollContainerRef={scrollRef}
      />

      <DocumentPageScrollWrapper ref={scrollRef}>
        {isDefined(documentCardStatus) && !isLoadingCheckrApplicationStatus && (
          <DocumentStatusCard
            variant={documentCardStatus}
            statusMessage={documentCardContent.statusMessage}
            expirationDate={isDefined(expiry) ? parseISO(expiry) : new Date()}
            title={documentCardContent.title}
            pillLabel={documentCardContent.pillLabel}
          >
            {status === WorkerRequirementStatus.REJECTED &&
              (isRunBackgroundCheckOnBookingEnabled ? (
                <Text variant="body1">
                  Didn&apos;t get an update after a few hours? Open a{" "}
                  <TextButton
                    size="large"
                    // To make sure the text is aligned with the rest of the text
                    sx={{
                      height: "inherit",
                      lineHeight: "inherit",
                      fontWeight: "inherit",
                      verticalAlign: "inherit",
                    }}
                    onClick={async () => {
                      await openZendeskMessaging();
                    }}
                  >
                    Support Chat
                  </TextButton>{" "}
                  and select <b>Documents or Onboarding → Background Check</b> to connect with a
                  team member who can assist you.
                </Text>
              ) : (
                <DocumentStatusCardActionButton
                  onClick={async () => {
                    await openArticle(backgroundChecksSupportLink);
                  }}
                >
                  Learn more
                </DocumentStatusCardActionButton>
              ))}
          </DocumentStatusCard>
        )}

        {isActionRequired &&
          (isLoadingCheckrApplicationStatus ? (
            <DocumentDetailsSectionSkeleton />
          ) : (
            isDefined(instructions) && (
              <DocumentDetailsSection title="Instructions" description={instructions} />
            )
          ))}

        <DocumentDetailsRequiredBySection />
      </DocumentPageScrollWrapper>

      {shouldShowCta && (
        <PageFooterWrapper>
          <Button
            fullWidth
            size="large"
            variant="contained"
            onClick={withPrerequisiteCheck(async () => {
              try {
                history.replace(
                  generatePath(`${DOCUMENT_DETAILS_FULL_PATH}/${DOCUMENT_CHECKR_LOADING_PATH}`, {
                    requirementId,
                  })
                );

                await openCheckrInvitation();

                void invalidateWorkerRequirements(queryClient);
                void invalidateCheckrApplicationStatus(queryClient);

                history.replace(
                  generatePath(`${DOCUMENT_DETAILS_FULL_PATH}`, {
                    requirementId,
                  })
                );
              } catch {
                history.replace(
                  generatePath(`${DOCUMENT_DETAILS_FULL_PATH}/${DOCUMENT_CHECKR_ERROR_PATH}`, {
                    requirementId,
                  })
                );
              }
            })}
          >
            Start background check
          </Button>
        </PageFooterWrapper>
      )}
      {prerequisiteBottomSheet}
    </PageWrapper>
  );
}
